/**
 * What kind of request this is and which auth it is really sent with — for
 * the in-app AI's `get_request` / `get_current_request` (04.10.2026, parity
 * with MCP cards 4 + 5).
 *
 * Before, both tools answered id / name / method / url and nothing else: a
 * gRPC request read as an empty-url GET, and "why does this 401?" could not
 * be answered because the model could not see that the request inherits a
 * folder's bearer token. The facts come from the MCP helpers so the assistant
 * and an MCP agent are told the same thing as Send does:
 *
 *  - auth: `authInheritanceOf` (src/mcp/authInheritance.js), which walks the
 *    enclosing folders with the app's own resolver. Types, folder ids/names
 *    and the `{{variable}}` NAMES a block is wired to — never a value.
 *  - protocol: `projectProtocol` (src/mcp/protocolProjection.js) for gRPC /
 *    WebSocket / SSE — address without userinfo, metadata and header values
 *    hidden unless they are pointers, the message as a SHAPE.
 */
import { authInheritanceOf } from '../../mcp/authInheritance';
import { projectProtocol, requestKind } from '../../mcp/protocolProjection';
import { findSidebarRequest, resolveRequestTabCollectionId } from '../../utils/requestResolve';

/** `{ type, auth, protocol }` for one stored request and its collection. */
export function requestFactsForModel(request, collection) {
  const a = authInheritanceOf(request || {}, collection || null);
  return {
    type: requestKind(request),
    auth: {
      effective_from: a.effectiveAuthFrom,
      effective_type: a.effectiveAuthType,
      effective_folder_id: a.effectiveAuthFolderId,
      applies_to_type: a.authAppliesToType,
      wired_to: a.effectiveAuth.wiredTo,
      literal_fields: a.effectiveAuth.literalFields,
      folder_chain: a.folderAuthChain.map((f) => ({ folder_id: f.folderId, name: f.name, type: f.auth.type })),
    },
    protocol: projectProtocol(request),
  };
}

/**
 * The same facts for the open request tab: the live draft over the tab over
 * the saved record (the folder lives on the saved record only).
 */
export function currentRequestFacts(tab, live, collections) {
  const saved = tab?.originalId != null ? findSidebarRequest(collections, tab.originalId) : null;
  const colId = resolveRequestTabCollectionId(tab, collections);
  const collection = (collections || []).find((c) => String(c.id) === String(colId)) || null;
  const merged = { ...(saved || {}), ...(tab || {}), ...(live || {}) };
  merged.folder_id = saved?.folder_id ?? merged.folder_id ?? null;
  return requestFactsForModel(merged, collection);
}
